import { Component } from '@angular/core';
import { MatDialog } from '@angular/material';
import { Router } from '@angular/router';
import { RawLeadStatuses, SalesModuleApiSuffixModels, Sales_Leads_Components, selectStaticEagerLoadingRawLeadStatusState$, selectStaticEagerLoadingSiteTypesState$ } from '@app/modules';
import { AppState } from "@app/reducers";
import {
  AgentLoginService,
  CrudService, CrudType,
  IApplicationResponse,
  LoggedInUserModel, ModulesBasedApiSuffix,
  PERMISSION_RESTRICTION_ERROR,
  ResponseMessageTypes,
  RxjsService,
  SnackbarService,
  addDefaultTableObjectProperties,
  agentLoginDataSelector,
  currentComponentPageBasedPermissionsSelector$,
  onActionSubmittedUtil,
  prepareDynamicTableTabsFromPermissions,
  prepareGetRequestHttpParams,
  replaceObjectKeysWithAlternate
} from '@app/shared';
import { loggedInUserData } from '@modules/others';
import { Store, select } from '@ngrx/store';
import { AddRawLeadsNotesModalComponent, ChangeStatusModalComponent } from '@sales/components/raw-lead';
import { combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { PrimeNgTableVariablesModel } from '../../../../shared/models/prime-ng-table-list-component-variables.model';

@Component({
  selector: 'app-raw-lead-list',
  templateUrl: './raw-lead-list.component.html',
  styleUrls: ['./raw-lead.component.scss'],
})

export class RawLeadListComponent extends PrimeNgTableVariablesModel {
  agentExtensionNo;
  primengTableConfigProperties: any = {
    tableCaption: "Raw Leads",
    breadCrumbItems: [{ displayName: 'Sales' }, { displayName: 'Raw Leads Dashboard' }],
    tableComponentConfigs: {
      tabsList: [
        {
          caption: 'Raw Leads',
          dataKey: 'rawLeadId',
          enableAction: true,
          enableUploadActionBtn: true,
          columns: [{ field: 'rawLeadRefNo', header: 'Raw Lead ID', width: '200px' },
          { field: 'client', header: 'Client Name', width: '200px' },
          { field: 'email', header: 'Email Address', width: '200px' },
          { field: 'phoneNumber', header: 'Phone Number', width: '200px', isTemplateRefOne: true },
          { field: 'sourceCode', header: 'Source Code', width: '200px' },
          { field: 'suburb', header: 'Suburb', width: '200px' },
          { field: 'callAttempts', header: 'Call Attempts', width: '200px' },
          { field: 'siteType', header: 'Site Type', width: '200px', type: 'dropdown', options: [] },
          { field: 'loadDate', header: 'Load Date', width: '200px', isDate: true },
          { field: 'iconClass', header: 'Notes', width: '200px', nosort: true, nofilter: true, isTemplateRefTwo: true },
          { field: 'status', header: 'Status', width: '200px', type: 'dropdown', options: [], statusClass: '', isStatusClick: true }]
        },
      ]
    }
  }

  constructor(
    private crudService: CrudService,
    private snackbarService: SnackbarService,
    private router: Router, private agentLoginService: AgentLoginService,
    private store: Store<AppState>,
    private dialog: MatDialog, private rxjsService: RxjsService) {
    super();
    this.primengTableConfigProperties = addDefaultTableObjectProperties(this.primengTableConfigProperties);
  }

  ngOnInit(): void {
    this.combineLatestNgrxStoreData();
    this.getRequiredListData();
    this.store.pipe(select(agentLoginDataSelector)).subscribe((extension: string) => {
      this.agentExtensionNo = extension;
    });
  }

  combineLatestNgrxStoreData(): void {
    combineLatest([
      this.store.select(loggedInUserData),
      this.store.select(selectStaticEagerLoadingSiteTypesState$),
      this.store.select(selectStaticEagerLoadingRawLeadStatusState$),
      this.store.select(currentComponentPageBasedPermissionsSelector$)]
    ).subscribe((response) => {
      this.loggedInUserData = new LoggedInUserModel(response[0]);
      this.primengTableConfigProperties.tableComponentConfigs.tabsList[0].columns[7].options = response[1] = response[1]?.map(item => {
        return { label: item.displayName, value: item.id };
      });
      this.primengTableConfigProperties.tableComponentConfigs.tabsList[0].columns[10].options = response[2] = response[2]?.map(item => {
        return { label: item.displayName, value: item.id };
      });
      if (response[3][Sales_Leads_Components.RAW_LEAD]) {
        let prepareDynamicTableTabsFromPermissionsObj =
          prepareDynamicTableTabsFromPermissions(this.primengTableConfigProperties, response[3][Sales_Leads_Components.RAW_LEAD]);
        this.primengTableConfigProperties = prepareDynamicTableTabsFromPermissionsObj['primengTableConfigProperties'];
      }
    });
  }

  addNotes(data): void {
    this.dialog.open(AddRawLeadsNotesModalComponent, { width: '700px', disableClose: true, data })
  }

  changeStatus(data) {
    if (data.rawLeadStatusId == RawLeadStatuses.Address_Verification_Pending || data.rawLeadStatusId == RawLeadStatuses.Archive ||
      data.rawLeadStatusId == RawLeadStatuses.Lead_Created) {
      return;
    }
    else if (data.status) {
      const dialogReff = this.dialog.open(ChangeStatusModalComponent, { width: '700px', disableClose: true, data });
      dialogReff.afterClosed().subscribe(result => {
        if (result) return;
        this.getRequiredListData();
      });
    }
  }

  onActionSubmitted(e) {
    if (e?.data?.rawLeadStatusId !== RawLeadStatuses.Archive) {
      onActionSubmittedUtil(e, this.onCRUDRequested.bind(this));
    }
  }

  onCRUDRequested(type: CrudType | string, row?: object, otherParams?): void {
    switch (type) {
      case CrudType.CREATE:
        this.openAddEditPage(CrudType.CREATE, row);
        break;
      case CrudType.GET:
        otherParams = replaceObjectKeysWithAlternate([{ actualKey: 'siteType', replaceableKey: 'siteTypeId' },
        { actualKey: 'status', replaceableKey: 'rawLeadStatusId' }], otherParams);
        this.getRequiredListData(row["pageIndex"], row["pageSize"], otherParams);
        break;
      case CrudType.VIEW:
        this.openAddEditPage(CrudType.VIEW, row);
        break;
      case CrudType.UPLOAD:
        this.openAddEditPage(CrudType.UPLOAD, row);
        break;
      case CrudType.ICON_POPUP:
        if (!this.primengTableConfigProperties.tableComponentConfigs.tabsList[this.selectedTabIndex].canEdit) {
          this.snackbarService.openSnackbar(PERMISSION_RESTRICTION_ERROR, ResponseMessageTypes.WARNING);
        }
        else {
          this.addNotes(row);
        }
        break;
      case CrudType.EDIT:
        if (!this.primengTableConfigProperties.tableComponentConfigs.tabsList[this.selectedTabIndex].canEdit) {
          this.snackbarService.openSnackbar(PERMISSION_RESTRICTION_ERROR, ResponseMessageTypes.WARNING);
        }
        else {
          this.changeStatus(row);
        }
        break;
    }
  }

  openAddEditPage(type: CrudType | string, editableObject?: object | string): void {
    this.rxjsService.setFromUrl('Raw Leads List');
    switch (type) {
      case CrudType.CREATE:
        if (!this.primengTableConfigProperties.tableComponentConfigs.tabsList[this.selectedTabIndex].canCreate) {
          this.snackbarService.openSnackbar(PERMISSION_RESTRICTION_ERROR, ResponseMessageTypes.WARNING);
        }
        else {
          this.router.navigateByUrl("/sales/raw-lead/add-edit");
        }
        break;
      case CrudType.VIEW:
        this.router.navigate(['sales/raw-lead/view'], { queryParams: { id: editableObject['rawLeadId'], leadStatus: editableObject['rawLeadStatusName'] } });
        break;
      case CrudType.UPLOAD:
        if (!this.primengTableConfigProperties.tableComponentConfigs.tabsList[this.selectedTabIndex].canUpload) {
          this.snackbarService.openSnackbar(PERMISSION_RESTRICTION_ERROR, ResponseMessageTypes.WARNING);
        }
        else {
          this.router.navigateByUrl("/sales/raw-lead/upload");
        }
        break;
    }
  }

  getRequiredListData(pageIndex?: string, pageSize?: string, otherParams?: object): void {
    this.loading = true;
    let salesModuleApiSuffixModels: SalesModuleApiSuffixModels;
    salesModuleApiSuffixModels = SalesModuleApiSuffixModels.SALES_API_RAW_LEADS;
    this.crudService.get(
      ModulesBasedApiSuffix.SALES,
      salesModuleApiSuffixModels,
      undefined, false, prepareGetRequestHttpParams(pageIndex, pageSize, otherParams)).
      pipe(map((res: IApplicationResponse) => {
        if (res?.resources) {
          res?.resources?.forEach(val => {
            val.callAttempts = val.callAttempts && val.callAttempts !== 0 ? val.callAttempts : '--';
            val.iconClass = 'icon icon-inventory notes-icon';
            return val;
          })
        }
        return res;
      })).subscribe((response: IApplicationResponse) => {
        if (response.isSuccess && response.statusCode === 200 && response.resources) {
          this.dataList = response.resources;
          this.totalRecords = response.totalCount;
        }
        this.loading = false;
        this.rxjsService.setGlobalLoaderProperty(false);
      });
  }

  contactNumber(contact) {
    if (!this.primengTableConfigProperties.tableComponentConfigs.tabsList[this.selectedTabIndex].canEdit) {
      this.snackbarService.openSnackbar(PERMISSION_RESTRICTION_ERROR, ResponseMessageTypes.WARNING);
    }
    else if (this.selectedTabIndex == 0) {
      if (!this.agentExtensionNo) {
        this.agentLoginService.openOpenscapeLoginPopup();
      } else {
        let data = {
          customerContactNumber: contact.phoneNumber,
          rawLeadId: contact.rawLeadId,
          clientName: contact.client
        };
        this.agentLoginService.setCustomerDataAndExpandOpenscapeDialpad(data);
      }
    }
  }
}